import React, { useState, useContext, useRef, useEffect } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../../../UserContext";
import { useNavigate } from "react-router-dom";
import { Avatar, Menu, MenuItem } from "@mui/material";
// import logo from "../../../assets/Stocai_logo_header.png";
import logo from "../../../assets/bclLogo.png";
import posthog from "posthog-js";
import { deleteUserCookie, setLogoutSignal } from "../../../utils";
import AdminDropdown from "./AdminDropdown";

const HeaderBar = () => {
  const { user } = useContext(UserContext);
  // const [login, setLogin] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  // const [resourcesAnchorEl, setResourcesAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBetaBanner, setShowBetaBanner] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  // const timeoutRef = useRef(null);
  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;
  const websiteURL = process.env.REACT_APP_WEBSITE_URL;
  const [activeSection, setActiveSection] = useState("hero-section");

  const menuRef = useRef(null);

  // Check if user is logged in (for protected navigation items)
  const isUserLoggedIn = user && user.user_id;

  const ROLE_IDS = {
    USER: "fe42ee60-9133-4f0e-b543-740d8697c23b",
    ADMIN: "ac9be322-db89-4beb-854a-46f2c91de374",
    SUPER_ADMIN: "5aee1e31-cc95-49d9-9ed7-10c4e4b6fdef",
  };

  const roleName =
    user?.role_name ||
    (user?.user_role === ROLE_IDS.ADMIN
      ? "Admin"
      : user?.user_role === ROLE_IDS.SUPER_ADMIN
        ? "Super Admin"
        : user?.user_role === ROLE_IDS.USER
          ? "User"
          : null);

  const isSuperAdmin = roleName === "Super Admin";
  const isAdmin = roleName === "Admin" || isSuperAdmin;
  const isLearner = roleName === "User" || (!roleName && isUserLoggedIn);

  const learnerNav = (() => {
    if (!isUserLoggedIn) return null;

    const map = {
      "/learner": "My Space",
      "/learner/learning": "Learning Path",
      "/learner/journey": "My Journey",
      "/learner/action-plan": "Action Plan",
      "/learner/reflection": "Reflection Room",
    };

    if (map[location.pathname]) {
      return { to: location.pathname, title: map[location.pathname] };
    }

    if (location.pathname.startsWith("/learner/")) {
      return { to: "/learner", title: "My Space" };
    }

    return { to: "/learner", title: "My Space" };
  })();

  const adminNav = (() => {
    if (!isUserLoggedIn) return null;
    if (!location.pathname.startsWith("/admin")) return null;

    const map = {
      "/admin": "Dashboard",
      "/admin/users": "Users",
      "/admin/waitlist": "Waitlist",
      "/admin/courses": "Courses",
      "/admin/cohorts": "Cohorts",
      "/admin/maintenance": "User Maintenance",
    };

    if (map[location.pathname]) {
      return { to: location.pathname, title: map[location.pathname] };
    }

    if (location.pathname.includes("/progress-summary")) {
      return { to: location.pathname, title: "Progress Summary" };
    }

    const parts = location.pathname.split("/").filter(Boolean);
    const section = parts[1];
    if (!section) return { to: "/admin", title: "Dashboard" };

    const title = section
      .split("-")
      .filter(Boolean)
      .map((s) => s[0]?.toUpperCase() + s.slice(1))
      .join(" ");

    return { to: location.pathname, title };
  })();

  const endSession = async () => {
    try {
      await fetch(`${apiBaseURL}/users/${user.user_id}/session/end`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log("Session Ended");
    } catch (error) {
      console.log("The session has ended:", error.message);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // setLogin(true);
    endSession();
    user.user_id = undefined;
    console.log(posthog.reset(true));

    // Set logout signal for cross-domain logout
    setLogoutSignal();

    setShowLogout(true);

    setTimeout(() => {
      setShowLogout(false);
      localStorage.removeItem("user");
      deleteUserCookie();
      window.location.href = "https://mystocai.com/"; // external redirect
    }, 3000);

    handleMenuClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById("hero-section");
      const sectionsToIgnore = [
        "how-it-works",
        "features",
        "testimonials",
        "faqs",
      ];

      if (heroSection) {
        const heroRect = heroSection.getBoundingClientRect();
        const isHeroVisible =
          heroRect.top <= window.innerHeight / 2 &&
          heroRect.bottom >= window.innerHeight / 2;

        if (isHeroVisible) {
          setActiveSection("hero-section");
          return;
        }
      }

      for (const section of sectionsToIgnore) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (
            rect.top <= window.innerHeight / 2 &&
            rect.bottom >= window.innerHeight / 2
          ) {
            setActiveSection(section);
            return;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (location.pathname === "/workspace") {
      setShowBetaBanner(false);
    }
  }, [location.pathname]);

  const handleNavigation = (scrollToId) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document
          .getElementById(scrollToId)
          ?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    } else {
      document
        .getElementById(scrollToId)
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogoClick = () => {
    localStorage.setItem("scrollToHero", "true");
    window.location.href = websiteURL || "http://localhost:3000";
  };

  const NavItem = ({ to, title, scrollToId, external }) => {
    const isActive =
      (title === "Home" &&
        location.pathname === "/" &&
        activeSection === "hero-section") ||
      (location.pathname === "/" && scrollToId === activeSection) ||
      (title === "My Space" && location.pathname === "/") ||
      (!scrollToId && location.pathname === to);

    // Handle external redirects to marketing website
    if (external) {
      return (
        <a
          href={`${process.env.REACT_APP_WEBSITE_URL}${to || ""}`}
          onClick={() => setMenuOpen(false)}
          className={`py-2 px-4 block lg:inline transition-colors duration-300 whitespace-nowrap ${
            isActive
              ? "bg-[#54B0AF] text-white font-semibold rounded-3xl"
              : "text-black hover:text-[#54B0AF] rounded-3xl"
          }`}
        >
          {title}
        </a>
      );
    }

    return scrollToId ? (
      <button
        onClick={() => handleNavigation(scrollToId)}
        className={`py-2 px-4 block lg:inline font-gotham text-base transition-colors duration-300 whitespace-nowrap ${
          isActive
            ? "bg-[#54B0AF] text-white font-semibold rounded-3xl"
            : "text-[#323232] hover:text-[#54B0AF] rounded-3xl"
        }`}
      >
        {title}
      </button>
    ) : (
      <Link
        to={to}
        onClick={() => setMenuOpen(false)}
        className={`py-2 px-4 block lg:inline font-gotham transition-colors duration-300 whitespace-nowrap ${
          isActive
            ? "bg-[#54B0AF] text-white font-semibold rounded-3xl"
            : "text-[#323232] hover:text-[#54B0AF] rounded-3xl"
        }`}
      >
        {title}
      </Link>
    );
  };

  return (
    <div
      className={`fixed left-0 w-full h-16 bg-[#F5F5F5] border-b border-[#DADADA] z-50 flex justify-between items-center px-4 sm:px-6 md:px-3 md:py-2 transition-all duration-300 ease-in-out ${
        showBetaBanner ? "top-[43px]" : "top-0"
      }`}
    >
      <div className="flex items-center gap-4">
        <img
          src={logo}
          alt="Logo"
          className="h-14 sm:h-16 lg:h-[80px] lg:w-[170px] w-auto rounded-full cursor-pointer"
          onClick={handleLogoClick}
        />

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-2">
          {isUserLoggedIn && (
            <>
              {(isLearner || isSuperAdmin) && learnerNav && (
                <NavItem to={learnerNav.to} title={learnerNav.title} />
              )}

              {(isAdmin || isSuperAdmin) && (
                <>
                  <NavItem to="/admin" title="Admin Panel" />
                  {adminNav && adminNav.to !== "/admin" && (
                    <NavItem to={adminNav.to} title={adminNav.title} />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <button
        className="lg:hidden text-[#323232] text-2xl"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        {menuOpen ? <FaTimes /> : <FaBars />}
      </button>

      <div
        ref={menuRef}
        className={`absolute flex items-center text-center md:items-start ml-5 lg:ml-0 top-16 lg:top-0 justify-center lg:relative left-0 w-full lg:w-auto lg:flex bg-[#F5F5F5] lg:bg-transparent flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-0 transition-transform duration-300 ease-in-out shadow-lg lg:shadow-none z-50 rounded-lg overflow-y-auto -translate-x-5 max-h-screen overflow-x-auto scrollbar-hide ${
          menuOpen ? "flex" : "hidden lg:hidden"
        }`}
      >
        {/* Mobile Navigation */}
        <div className="block lg:hidden">
          {isUserLoggedIn && (
            <>
              {(isLearner || isSuperAdmin) && learnerNav && (
                <NavItem to={learnerNav.to} title={learnerNav.title} />
              )}

              {(isAdmin || isSuperAdmin) && (
                <>
                  <NavItem to="/admin" title="Admin Panel" />
                  {adminNav && adminNav.to !== "/admin" && (
                    <NavItem to={adminNav.to} title={adminNav.title} />
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="block lg:hidden w-full text-center">
          {user.user_id ? (
            <button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 my-2 py-2 px-4 text-white rounded-full w-[40%]"
            >
              Logout
            </button>
          ) : (
            <button
              onClick={() => {
                navigate("/signUp");
                setMenuOpen(false);
              }}
              className="bg-[#54B0AF] my-2 py-2 px-4 text-white rounded-full w-[40%]"
            >
              Login
            </button>
          )}
        </div>
      </div>

      {/* Admin Dropdown + Avatar / Login Button */}
      <div className="hidden lg:flex items-center gap-2">
        <AdminDropdown user={user} />
        {user.user_id ? (
          <>
            <Avatar
              sx={{ bgcolor: "#54B0AF", cursor: "pointer" }}
              onClick={handleMenuOpen}
              title="User Menu"
            >
              {user?.firstName?.[0]?.toUpperCase() || "U"}
            </Avatar>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={handleLogout}>
                <span className="flex items-center font-gotham font-semibold">
                  Logout
                </span>
              </MenuItem>
            </Menu>
          </>
        ) : (
          <button
            className="bg-[#54B0AF] py-2 font-medium text-white text-lg px-7 rounded-full"
            onClick={() => navigate("/signUp")}
            title="Login"
          >
            Login
          </button>
        )}
      </div>

      {/* Logout Notification */}
      {showLogout && (
        <div className="fixed top-24 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-[100] animate-fade-in">
          <p className="font-gotham font-semibold">Logging out...</p>
        </div>
      )}
    </div>
  );
};

export default HeaderBar;
